import type { ColorLevel } from 'src/models/enum/ColorLevel';
import { uid } from 'quasar';
export type InfoLevel = keyof typeof ColorLevel | null;
export type Option = {
  local_id: string;
  value: string;
  info_level: InfoLevel;
  questions?: Question[];
};
type QuestionTypeRadio = 'radio';
type QuestionTypeCheck = 'check';
type QuestionTypeText = 'text';
type QuestionTypeNumber = 'number';
type QuestionTypeImage = 'image';
type QuestionTypeDate = 'date';
type QuestionTypeTime = 'time';
type QuestionTypeDateTime = 'datetime';
export type QuestionType =
  | QuestionTypeRadio
  | QuestionTypeCheck
  | QuestionTypeText
  | QuestionTypeNumber
  | QuestionTypeImage
  | QuestionTypeDate
  | QuestionTypeTime
  | QuestionTypeDateTime;
export type Question =
  | RadioQuestion
  | CheckQuestion
  | TextQuestion
  | NumberQuestion
  | ImageQuestion
  | DateTimeQuestion
  | DateQuestion
  | TimeQuestion;
export type Answer<T> = T | null;
export type CheckAnswer = {
  value: string;
  info_level: InfoLevel;
  option_local_id: string;
};
export type CheckAnswers = CheckAnswer[];
export type RadioAnswer = {
  value: string;
  info_level: InfoLevel;
  option_local_id: string;
};
export type NumberAnswer = {
  value: number;
  unit: string;
};
export type BasicAnswer = {
  value: string;
};
export type BasicAnswers = BasicAnswer[];
type CommonQuestion = {
  local_id: string;
  question_text: string;
};
type RadioQuestion = CommonQuestion & {
  type: QuestionTypeRadio;
  options: Option[];
  answer?: Answer<RadioAnswer>;
};
type CheckQuestion = CommonQuestion & {
  type: QuestionTypeCheck;
  options: Option[];
  answer?: Answer<CheckAnswers>;
};
type NumberQuestion = CommonQuestion & {
  type: QuestionTypeNumber;
  unit: string;
  answer?: Answer<NumberAnswer>;
};
type TextQuestion = CommonQuestion & {
  type: QuestionTypeText;
  answer?: Answer<BasicAnswer>;
};
type ImageQuestion = CommonQuestion & {
  type: QuestionTypeImage;
  answer?: Answer<BasicAnswers>;
};
type DateQuestion = CommonQuestion & {
  type: QuestionTypeDate;
  answer?: Answer<BasicAnswer>;
};
type TimeQuestion = CommonQuestion & {
  type: QuestionTypeTime;
  answer?: Answer<BasicAnswer>;
};
type DateTimeQuestion = CommonQuestion & {
  type: QuestionTypeDateTime;
  answer?: Answer<BasicAnswer>;
};
export class QuestionTemplateModel {
  id: string;
  question_category_id: string;
  question: Question;
  constructor(input?: QuestionTemplateGetAttributes) {
    this.id = input ? input.id : '';
    this.question_category_id = input ? input.question_category_id : '';
    this.question = input
      ? this.getQuestion(input.question)
      : getInitRadioQuestion();
  }
  getCreateAttributes(): QuestionTemplateSendAttributes {
    return {
      question_category_id: this.question_category_id,
      question: this.getQuestion(this.question),
    };
  }
  getUpdateAttributes(): QuestionTemplateSendAttributes {
    return {
      question_category_id: this.question_category_id,
      question: this.getQuestion(this.question),
    };
  }
  getQuestion(question: Question): Question {
    if (question.type == 'radio') {
      return {
        local_id: question.local_id,
        type: question.type,
        question_text: question.question_text,
        options: question.options.map((option) => this.getOption(option)),
        answer: question.answer,
      };
    } else if (question.type == 'check') {
      return {
        local_id: question.local_id,
        type: question.type,
        question_text: question.question_text,
        options: question.options.map((option) => this.getOption(option)),
        answer: question.answer,
      };
    } else if (question.type == 'number') {
      return {
        local_id: question.local_id,
        type: question.type,
        question_text: question.question_text,
        unit: question.unit,
        answer: question.answer,
      };
    } else {
      return {
        local_id: question.local_id,
        type: question.type,
        question_text: question.question_text,
        answer: question.answer,
      } as Question;
    }
  }
  getOption(option: Option): Option {
    if (option.questions) {
      return {
        local_id: option.local_id,
        value: option.value,
        info_level: option.info_level ? option.info_level : null,
        questions: option.questions.map((question) =>
          this.getQuestion(question)
        ),
      };
    } else {
      return {
        local_id: option.local_id,
        value: option.value,
        info_level: option.info_level ? option.info_level : null,
      };
    }
  }
}
export interface QuestionTemplateGetAttributes {
  id: string;
  question_category_id: string;
  question: Question;
}
export interface QuestionTemplateSendAttributes {
  question_category_id: string;
  question: Question;
}
export const getInitRadioQuestion = (): RadioQuestion => {
  return {
    local_id: uid(),
    type: 'radio',
    question_text: '選択肢から1つ選んでください',
    options: getInitOptions(),
    answer: null,
  };
};

export const getInitOptions = (): Option[] => {
  return [
    {
      local_id: uid(),
      value: 'はい',
      info_level: 4,
    },
    {
      local_id: uid(),
      value: 'いいえ',
      info_level: 1,
    },
  ];
};
